import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Build as BuildIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  SmartToy as SmartToyIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Add as AddIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { maintenanceAPI, workerAPI, hodAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import Layout from "../common/Layout";
import NotificationButton from "../common/NotificationButton";

/**
 * ManagerDashboard
 * Global maintenance manager (role: manager) overview of all maintenance requests.
 */
const ManagerDashboard = () => {
  const { currentUser, userProfile } = useAuth(); // Get both user and profile
  const { sendNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    assigned: 0,
    in_progress: 0,
    resolved: 0,
    rejected: 0,
    cancelled: 0,
  });

  // State for clickable widgets
  const [filterDialog, setFilterDialog] = useState({
    open: false,
    title: "",
    requests: [],
    filterType: "",
  });

  const [requestDetailDialog, setRequestDetailDialog] = useState({
    open: false,
    request: null,
  });

  // State for request management dialog
  const [managementDialog, setManagementDialog] = useState({
    open: false,
    request: null,
    workers: [],
    selectedWorker: "",
    newStatus: "",
    notes: "",
    loading: false,
  });

  // State for HOD management
  const [hods, setHods] = useState([]);
  const [hodCreateDialog, setHodCreateDialog] = useState({
    open: false,
    loading: false,
  });
  const [hodFormData, setHodFormData] = useState({
    name: "",
    email: "",
    phone: "",
    category: "",
    departmentId: "",
    departmentName: "",
    experienceYears: "",
    qualifications: "",
    maxTeamSize: "10",
  });

  // Delete HOD dialog state
  const [deleteHodDialog, setDeleteHodDialog] = useState({
    open: false,
    hod: null,
    loading: false,
  });

  // Worker management state
  const [workers, setWorkers] = useState([]);
  const [deleteWorkerDialog, setDeleteWorkerDialog] = useState({
    open: false,
    worker: null,
    loading: false,
  });

  // Worker creation state
  const [workerCreateDialog, setWorkerCreateDialog] = useState({
    open: false,
    loading: false,
  });

  const [workerFormData, setWorkerFormData] = useState({
    name: "",
    email: "",
    phone: "",
    category: "",
    specialization: [], // Changed from specializations to specialization
    experienceYears: 0,
    qualifications: "",
    contactPreference: "phone",
    maxConcurrentTasks: 5,
    workingHours: {
      start: "09:00",
      end: "17:00",
      workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
  });

  const [activeTab, setActiveTab] = useState(0); // 0: Requests, 1: Maintenance HODs, 2: Maintenance Workers  // Handler for widget clicks
  const handleWidgetClick = (filterType, title) => {
    let filteredRequests = [];

    switch (filterType) {
      case "total":
        filteredRequests = requests;
        break;
      case "pending":
        filteredRequests = requests.filter((req) => req.status === "pending");
        break;
      case "assigned":
        filteredRequests = requests.filter((req) => req.status === "assigned");
        break;
      case "in_progress":
        filteredRequests = requests.filter(
          (req) => req.status === "in_progress"
        );
        break;
      case "resolved":
        filteredRequests = requests.filter((req) => req.status === "resolved");
        break;
      case "autoAssigned":
        filteredRequests = requests.filter((req) => req.autoAssigned === true);
        break;
      case "manualOverrides":
        filteredRequests = requests.filter(
          (req) => req.manuallyOverridden === true
        );
        break;
      default:
        filteredRequests = requests;
    }

    // Apply the same sorting logic to filtered results
    const sortedFilteredRequests = filteredRequests.sort((a, b) => {
      // Priority 1: Most recent assignments (assignedAt)
      if (a.assignedAt && b.assignedAt) {
        return new Date(b.assignedAt) - new Date(a.assignedAt);
      }
      if (a.assignedAt && !b.assignedAt) return -1;
      if (!a.assignedAt && b.assignedAt) return 1;

      // Priority 2: Most recent updates (updatedAt)
      if (a.updatedAt && b.updatedAt) {
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
      if (a.updatedAt && !b.updatedAt) return -1;
      if (!a.updatedAt && b.updatedAt) return 1;

      // Priority 3: Most recent creation (createdAt)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    setFilterDialog({
      open: true,
      title: title,
      requests: sortedFilteredRequests,
      filterType: filterType,
    });
  };

  const closeFilterDialog = () => {
    setFilterDialog({
      open: false,
      title: "",
      requests: [],
      filterType: "",
    });
  };

  const handleRequestClick = (request) => {
    setRequestDetailDialog({
      open: true,
      request: request,
    });
  };

  const closeRequestDetailDialog = () => {
    setRequestDetailDialog({
      open: false,
      request: null,
    });
  };

  // Management dialog handlers
  const openManagementDialog = async (request) => {
    setManagementDialog({
      open: true,
      request: request,
      workers: [],
      selectedWorker: request.assignedWorkerId || "",
      newStatus: request.status || "",
      notes: "",
      loading: true,
    });

    // Fetch available workers
    try {
      const { data } = await workerAPI.getAllWorkers();
      setManagementDialog((prev) => ({
        ...prev,
        workers: data || [],
        loading: false,
      }));
    } catch (error) {
      console.error("Failed to fetch workers:", error);
      setManagementDialog((prev) => ({
        ...prev,
        workers: [],
        loading: false,
      }));
    }
  };

  const closeManagementDialog = () => {
    setManagementDialog({
      open: false,
      request: null,
      workers: [],
      selectedWorker: "",
      newStatus: "",
      notes: "",
      loading: false,
    });
  };

  const handleSaveChanges = async () => {
    const { request, selectedWorker, newStatus, notes } = managementDialog;

    setManagementDialog((prev) => ({ ...prev, loading: true }));

    try {
      const updateData = {
        status: newStatus,
        notes: notes,
      };

      let workerAssignmentChanged = false;
      let workerName = "";

      // If worker assignment changed
      if (selectedWorker !== request.assignedWorkerId) {
        workerAssignmentChanged = true;
        if (selectedWorker) {
          updateData.assignedWorkerId = selectedWorker;
          updateData.manuallyOverridden = true;
          // Find worker name for notification
          const assignedWorker = managementDialog.workers.find(
            (w) => w.id === selectedWorker
          );
          workerName = assignedWorker?.name || "Unknown Worker";
        } else {
          updateData.assignedWorkerId = null;
          updateData.manuallyOverridden = false;
        }
      }

      await maintenanceAPI.updateRequest(request.id, updateData);

      // Send notifications based on changes made
      try {
        // If worker was assigned/reassigned
        if (workerAssignmentChanged && selectedWorker) {
          sendNotification("request_assigned", {
            id: request.id,
            tokenNumber: request.tokenNumber,
            workerName: workerName,
            category: request.category,
            priority: request.priority || "Medium",
          });
        }

        // If status was changed
        if (newStatus !== request.status) {
          sendNotification("request_status_updated", {
            id: request.id,
            tokenNumber: request.tokenNumber,
            status: newStatus,
            category: request.category,
            requesterName: request.requesterName,
          });
        }

        // Special notification for urgent requests or completion
        if (newStatus === "resolved") {
          sendNotification("request_completed", {
            id: request.id,
            tokenNumber: request.tokenNumber,
            category: request.category,
            requesterName: request.requesterName,
          });
        }
      } catch (notificationError) {
        console.warn("Failed to send notifications:", notificationError);
        // Don't fail the main operation if notifications fail
      }

      // Refresh the requests
      await fetchAll();

      // Close dialogs
      closeManagementDialog();
      closeRequestDetailDialog();
    } catch (error) {
      console.error("Failed to update request:", error);
      setError(
        "Failed to update request: " +
          (error.response?.data?.message || error.message)
      );
      setManagementDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchAll = async () => {
    console.log("[DEBUG] fetchAll() called - starting data refresh");
    setLoading(true);
    setError("");
    try {
      console.log("[DEBUG] Calling maintenanceAPI.getAllRequests()");
      const { data } = await maintenanceAPI.getAllRequests();
      console.log("[DEBUG] API Response received:", {
        dataExists: !!data,
        dataLength: data?.length || 0,
        firstRequest: data?.[0]
          ? {
              tokenNumber: data[0].tokenNumber,
              status: data[0].status,
              category: data[0].category,
              createdAt: data[0].createdAt,
            }
          : "No requests",
      });

      // Sort requests by most recent activity first
      const sortedData = (data || []).sort((a, b) => {
        // Priority 1: Most recent creation (for new requests)
        const aCreated = new Date(a.createdAt);
        const bCreated = new Date(b.createdAt);

        // Priority 2: Most recent assignments (assignedAt)
        const aAssigned = a.assignedAt ? new Date(a.assignedAt) : null;
        const bAssigned = b.assignedAt ? new Date(b.assignedAt) : null;

        // Priority 3: Most recent updates (updatedAt)
        const aUpdated = a.updatedAt ? new Date(a.updatedAt) : null;
        const bUpdated = b.updatedAt ? new Date(b.updatedAt) : null;

        // Get the most recent activity timestamp for each request
        const aLatestActivity = Math.max(
          aCreated.getTime(),
          aAssigned?.getTime() || 0,
          aUpdated?.getTime() || 0
        );

        const bLatestActivity = Math.max(
          bCreated.getTime(),
          bAssigned?.getTime() || 0,
          bUpdated?.getTime() || 0
        );

        // Sort by most recent activity (creation, assignment, or update)
        return bLatestActivity - aLatestActivity;
      });

      console.log(
        "[DEBUG] Maintenance requests sorted by most recent activity (creation/assignment/update)"
      );
      console.log("[DEBUG] Top 5 requests by latest activity:");

      // Enhanced logging to show latest activity for each request
      sortedData.slice(0, 5).forEach((req, index) => {
        const activities = [];
        const timestamps = [];

        // Track all activity timestamps
        if (req.createdAt) {
          activities.push(
            `created ${new Date(req.createdAt).toLocaleString()}`
          );
          timestamps.push(new Date(req.createdAt).getTime());
        }
        if (req.assignedAt) {
          activities.push(
            `assigned ${new Date(req.assignedAt).toLocaleString()}`
          );
          timestamps.push(new Date(req.assignedAt).getTime());
        }
        if (req.updatedAt && req.updatedAt !== req.createdAt) {
          activities.push(
            `updated ${new Date(req.updatedAt).toLocaleString()}`
          );
          timestamps.push(new Date(req.updatedAt).getTime());
        }

        const latestTimestamp = Math.max(...timestamps);
        const latestActivity = new Date(latestTimestamp).toLocaleString();

        console.log(
          `[DEBUG] ${index + 1}. ${req.tokenNumber} (${
            req.category
          }) - Latest: ${latestActivity}`
        );
        console.log(`    └─ All activities: ${activities.join(", ")}`);

        if (req.manuallyOverridden)
          console.log(`    └─ MANAGER OVERRIDE detected`);
        if (req.autoAssigned) console.log(`    └─ AUTO-ASSIGNED by system`);
        if (req.assignedWorkerName)
          console.log(`    └─ Assigned to: ${req.assignedWorkerName}`);
      });

      console.log(
        "[DEBUG] Setting requests state with",
        sortedData.length,
        "requests"
      );
      setRequests(sortedData);

      const aggregated = sortedData.reduce(
        (acc, r) => {
          acc.total++;
          acc[r.status] = (acc[r.status] || 0) + 1;
          if (r.autoAssigned) acc.autoAssigned = (acc.autoAssigned || 0) + 1;
          if (r.manuallyOverridden)
            acc.manualOverrides = (acc.manualOverrides || 0) + 1;
          return acc;
        },
        {
          total: 0,
          pending: 0,
          assigned: 0,
          in_progress: 0,
          resolved: 0,
          rejected: 0,
          cancelled: 0,
          autoAssigned: 0,
          manualOverrides: 0,
        }
      );

      // Log card statistics for debugging
      console.log("[DEBUG] Updating dashboard cards:");
      console.log(`[DEBUG] Total Requests: ${aggregated.total}`);
      console.log(`[DEBUG] Pending: ${aggregated.pending || 0}`);
      console.log(`[DEBUG] Assigned: ${aggregated.assigned || 0}`);
      console.log(`[DEBUG] In Progress: ${aggregated.in_progress || 0}`);
      console.log(`[DEBUG] Resolved: ${aggregated.resolved || 0}`);
      console.log(`[DEBUG] Auto-Assigned: ${aggregated.autoAssigned || 0}`);
      console.log(
        `[DEBUG] Manual Overrides: ${aggregated.manualOverrides || 0}`
      );

      // Notify if card values changed
      const previousTotal = stats.total || 0;
      const previousPending = stats.pending || 0;
      const previousAssigned = stats.assigned || 0;

      if (aggregated.total !== previousTotal) {
        console.log(
          `[CARD UPDATE] Total requests changed from ${previousTotal} to ${aggregated.total}`
        );
      }
      if ((aggregated.pending || 0) !== previousPending) {
        console.log(
          `[CARD UPDATE] Pending requests changed from ${previousPending} to ${
            aggregated.pending || 0
          }`
        );
      }
      if ((aggregated.assigned || 0) !== previousAssigned) {
        console.log(
          `[CARD UPDATE] Assigned requests changed from ${previousAssigned} to ${
            aggregated.assigned || 0
          }`
        );
      }

      console.log("[DEBUG] Setting stats state:", aggregated);
      console.log("[DEBUG] Previous stats:", stats);
      setStats(aggregated);
    } catch (e) {
      setError(
        e.response?.data?.message || "Failed to load maintenance requests"
      );
    } finally {
      setLoading(false);
    }
  };

  // Debug: Track stats changes
  useEffect(() => {
    console.log("[DEBUG] Stats state updated:", stats);
  }, [stats]);

  // Debug: Track requests changes
  useEffect(() => {
    console.log("[DEBUG] Requests state updated, length:", requests.length);
  }, [requests]);

  useEffect(() => {
    console.log(
      "[DEBUG] ManagerDashboard component mounted - initializing data fetch"
    );
    fetchAll();
    fetchHods();
    fetchWorkers();

    // Set up periodic refresh to catch new requests and HOD activities
    const refreshInterval = setInterval(() => {
      console.log(
        "[DEBUG] Auto-refreshing Manager Dashboard at",
        new Date().toLocaleTimeString(),
        "- checking for new requests and HOD activities"
      );
      fetchAll();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line
  }, []);

  // HOD Management Functions
  const fetchHods = async () => {
    try {
      console.log("[DEBUG] fetchHods - Starting...");
      console.log("[DEBUG] Current user:", currentUser);
      console.log("[DEBUG] User role:", currentUser?.role);

      const response = await hodAPI.getAllHODs();
      console.log("[DEBUG] HODs response:", response.data);

      // Backend returns HODs array directly, not wrapped in an object
      const hodsArray = Array.isArray(response.data) ? response.data : [];
      console.log("[DEBUG] Final HODs array:", hodsArray);
      console.log("[DEBUG] HODs array length:", hodsArray.length);

      setHods(hodsArray);
    } catch (error) {
      console.error("Failed to fetch HODs:", error);

      if (error.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else if (error.response?.status === 403) {
        setError("Access denied. Manager permissions required.");
      } else {
        setError(
          "Failed to fetch HODs: " +
            (error.response?.data?.message || error.message)
        );
      }

      setHods([]);
    }
  };

  const openHodCreateDialog = () => {
    console.log("[DEBUG] userProfile:", userProfile);
    console.log("[DEBUG] userProfile.departmentId:", userProfile?.departmentId);
    console.log("[DEBUG] userProfile.department:", userProfile?.department);

    setHodCreateDialog({ open: true, loading: false });
    setHodFormData({
      name: "",
      email: "",
      phone: "",
      category: "",
      departmentId: userProfile?.departmentId || "",
      departmentName: userProfile?.department || "",
      experienceYears: "",
      qualifications: "",
      maxTeamSize: "10",
    });
  };

  const closeHodCreateDialog = () => {
    setHodCreateDialog({ open: false, loading: false });
  };

  const handleHodFormChange = (field, value) => {
    setHodFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateHod = async () => {
    setHodCreateDialog((prev) => ({ ...prev, loading: true }));

    try {
      console.log("[DEBUG] Creating HOD with data:", hodFormData);
      console.log("[DEBUG] Current user:", currentUser);
      console.log("[DEBUG] User role:", currentUser?.role);

      const response = await hodAPI.createHOD(hodFormData);
      console.log("[DEBUG] HOD created successfully:", response.data);

      setSuccess(`HOD ${hodFormData.name} created successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);

      console.log("[DEBUG] Refreshing HODs list...");
      await fetchHods(); // Refresh HOD list
      console.log("[DEBUG] HODs list refreshed");

      closeHodCreateDialog();
    } catch (error) {
      console.error("Failed to create HOD:", error);

      if (error.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else if (error.response?.status === 403) {
        setError("Access denied. Manager permissions required to create HODs.");
      } else {
        setError(
          "Failed to create HOD: " +
            (error.response?.data?.message || error.message)
        );
      }

      // Clear error message after 5 seconds
      setTimeout(() => setError(""), 5000);
    } finally {
      setHodCreateDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Delete HOD function
  const handleDeleteHod = async () => {
    if (!deleteHodDialog.hod) return;

    setDeleteHodDialog((prev) => ({ ...prev, loading: true }));

    try {
      console.log("[DEBUG] Deleting HOD:", deleteHodDialog.hod.id);

      // Use hodAPI instead of raw fetch for consistency
      await hodAPI.deleteHOD(deleteHodDialog.hod.id);
      console.log("[DEBUG] HOD deleted successfully");

      setSuccess(`HOD ${deleteHodDialog.hod.name} deleted successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);

      // Close dialog
      setDeleteHodDialog({
        open: false,
        hod: null,
        loading: false,
      });

      // Refresh HODs list
      console.log("[DEBUG] Refreshing HODs list after deletion...");
      await fetchHods();
      console.log("[DEBUG] HODs list refreshed after deletion");
    } catch (error) {
      console.error("Failed to delete HOD:", error);

      if (error?.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else if (error?.response?.status === 403) {
        setError("Access denied. Manager permissions required to delete HODs.");
      } else if (error?.response?.status === 400) {
        const errorMsg =
          error.response?.data?.message ||
          "Cannot delete HOD due to active assignments";
        setError(`Failed to delete HOD: ${errorMsg}`);
      } else {
        setError(
          "Failed to delete HOD: " +
            (error.response?.data?.message || error.message)
        );
      }

      setDeleteHodDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Worker creation function
  const handleCreateWorker = async () => {
    if (
      !workerFormData.name ||
      !workerFormData.email ||
      !workerFormData.phone ||
      !workerFormData.category
    ) {
      setError("Name, email, phone, and category are required");
      return;
    }

    try {
      setWorkerCreateDialog((prev) => ({ ...prev, loading: true }));

      // Use workerAPI for consistency - only send fields that server expects
      const workerPayload = {
        name: workerFormData.name,
        email: workerFormData.email,
        phone: workerFormData.phone,
        category: workerFormData.category,
        specialization: workerFormData.specialization, // Server expects singular form
        experienceYears: parseInt(workerFormData.experienceYears) || 0,
        qualifications: workerFormData.qualifications,
        contactPreference: workerFormData.contactPreference,
        maxConcurrentTasks: parseInt(workerFormData.maxConcurrentTasks) || 5,
        workingHours: workerFormData.workingHours,
      };

      console.log("[DEBUG] Creating worker with payload:", workerPayload);
      const response = await workerAPI.createWorker(workerPayload);
      console.log("[DEBUG] Worker created successfully:", response);

      // Refresh workers list
      await fetchWorkers();

      // Close dialog and reset form
      setWorkerCreateDialog({ open: false, loading: false });
      setWorkerFormData({
        name: "",
        email: "",
        phone: "",
        category: "",
        specialization: [],
        experienceYears: 0,
        qualifications: "",
        contactPreference: "phone",
        maxConcurrentTasks: 5,
        workingHours: {
          start: "09:00",
          end: "17:00",
          workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        },
      });

      setSuccess(
        `Worker ${
          response.data?.name || workerFormData.name
        } created successfully`
      );
    } catch (error) {
      console.error("Worker creation error:", error);

      if (error?.response?.status === 401) {
        setError(
          "Authentication failed. Please check your login credentials and permissions."
        );
      } else if (error?.response?.status === 403) {
        setError(
          "Permission denied. You don't have access to create workers. Please contact system administrator."
        );
      } else if (error?.response?.status === 400) {
        const errorMsg =
          error.response?.data?.message ||
          (error.response?.data?.errors
            ? error.response.data.errors.map((e) => e.msg).join(", ")
            : "Invalid data provided");
        setError(`Worker creation failed: ${errorMsg}`);
      } else if (error?.response?.data?.message) {
        setError(`Worker creation failed: ${error.response.data.message}`);
      } else {
        setError(`Worker creation failed: ${error.message || "Unknown error"}`);
      }
    } finally {
      setWorkerCreateDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Worker Management Functions
  const fetchWorkers = async () => {
    try {
      console.log("[DEBUG] fetchWorkers - Starting...");
      console.log("[DEBUG] Current user:", currentUser);
      console.log("[DEBUG] User role:", currentUser?.role);

      const response = await workerAPI.getAllWorkers();
      console.log("[DEBUG] Workers response:", response);
      setWorkers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch workers:", error);

      if (error.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else if (error.response?.status === 403) {
        setError("Access denied. Manager permissions required.");
      } else {
        setError(
          "Failed to fetch workers: " +
            (error.response?.data?.message || error.message)
        );
      }

      setWorkers([]);
    }
  };

  // Open delete worker dialog
  const openDeleteWorkerDialog = (worker) => {
    setDeleteWorkerDialog({
      open: true,
      worker: worker,
      loading: false,
    });
  };

  // Delete worker function
  const handleDeleteWorker = async () => {
    if (!deleteWorkerDialog.worker) return;

    setDeleteWorkerDialog((prev) => ({ ...prev, loading: true }));

    try {
      console.log("[DEBUG] Deleting worker:", deleteWorkerDialog.worker.id);

      const response = await workerAPI.deleteWorker(
        deleteWorkerDialog.worker.id
      );
      console.log("[DEBUG] Worker deletion response:", response);

      setSuccess(
        `Worker ${deleteWorkerDialog.worker.name} deleted successfully`
      );

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);

      // Close dialog
      setDeleteWorkerDialog({
        open: false,
        worker: null,
        loading: false,
      });

      // Refresh workers list
      console.log("[DEBUG] Refreshing workers list after deletion...");
      await fetchWorkers();
      console.log("[DEBUG] Workers list refreshed after deletion");
    } catch (error) {
      console.error("Failed to delete worker:", error);
      setError("Failed to delete worker: " + error.message);
      setDeleteWorkerDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Layout title="Maintenance Manager">
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <BuildIcon /> Maintenance Manager Dashboard
          </Typography>

          {/* Notification Controls */}
          <NotificationButton showLabel={true} size="medium" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Global overview of all maintenance requests and HOD management. As a
          Maintenance Manager, you have full maintenance superuser privileges
          including the ability to override auto-assignments, manage all
          workers, and create HOD accounts.
        </Typography>

        {/* Tabs for different sections */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="manager dashboard tabs"
          >
            <Tab
              label="Maintenance Requests"
              icon={<BuildIcon />}
              iconPosition="start"
            />
            <Tab
              label="Maintenance HODs"
              icon={<SupervisorAccountIcon />}
              iconPosition="start"
            />
            <Tab
              label="Maintenance Workers"
              icon={<PersonIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {/* Auto-assignment info */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Auto-Assignment Active:</strong> New maintenance
                requests are automatically assigned to workers based on their
                specializations and availability. You can reassign workers at
                any time using the "Reassign Worker" button.
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { key: "total", icon: <AssignmentIcon />, color: "primary" },
                { key: "pending", icon: <ScheduleIcon />, color: "warning" },
                { key: "assigned", icon: <AssignmentIcon />, color: "info" },
                {
                  key: "in_progress",
                  icon: <ScheduleIcon />,
                  color: "secondary",
                },
                {
                  key: "resolved",
                  icon: <CheckCircleIcon />,
                  color: "success",
                },
                { key: "autoAssigned", icon: <SmartToyIcon />, color: "info" },
                {
                  key: "manualOverrides",
                  icon: <EditIcon />,
                  color: "warning",
                },
              ].map(({ key, icon, color }) => {
                const getTitle = (key) => {
                  switch (key) {
                    case "autoAssigned":
                      return "Auto-Assigned Requests";
                    case "manualOverrides":
                      return "Manual Override Requests";
                    default:
                      return (
                        key
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()) +
                        " Requests"
                      );
                  }
                };

                const getLabel = (key) => {
                  switch (key) {
                    case "autoAssigned":
                      return "Auto-Assigned";
                    case "manualOverrides":
                      return "Manual Overrides";
                    default:
                      return key.replace("_", " ");
                  }
                };

                return (
                  <Grid item xs={12} sm={6} md={3} lg={2} key={key}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleWidgetClick(key, getTitle(key))}
                        sx={{ height: "100%" }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ color: `${color}.main`, mr: 1 }}>
                              {icon}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                textTransform: "uppercase",
                                fontWeight: "bold",
                              }}
                            >
                              {getLabel(key)}
                            </Typography>
                          </Box>
                          <Typography
                            variant="h4"
                            color={`${color}.main`}
                            fontWeight="bold"
                          >
                            {stats[key] || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Click to view details
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchAll}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </Box>
            {loading && <CircularProgress />}
            {!loading && (
              <Grid container spacing={2}>
                {requests.map((req) => (
                  <Grid item xs={12} md={6} lg={4} key={req.id}>
                    <Card
                      sx={{
                        height: "100%",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleRequestClick(req)}
                        sx={{ height: "100%" }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={600}>
                              {req.tokenNumber}
                            </Typography>
                            <Chip
                              size="small"
                              label={req.status}
                              color={
                                req.status === "resolved"
                                  ? "success"
                                  : req.status === "pending"
                                  ? "warning"
                                  : req.status === "in_progress"
                                  ? "secondary"
                                  : "default"
                              }
                            />
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Category:</strong> {req.category}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Subcategory:</strong>{" "}
                            {req.subcategory?.replace(/_/g, " ") || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Location:</strong> {req.location}
                          </Typography>
                          <Typography
                            variant="body2"
                            noWrap
                            title={req.description}
                          >
                            <strong>Description:</strong> {req.description}
                          </Typography>
                          {req.assignedWorkerName && (
                            <Typography variant="body2">
                              <strong>Assigned to:</strong>{" "}
                              {req.assignedWorkerName}
                              {req.autoAssigned && (
                                <Chip
                                  label="Auto"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{
                                    ml: 1,
                                    fontSize: "0.6rem",
                                    height: "16px",
                                  }}
                                />
                              )}
                              {req.manuallyOverridden && (
                                <Chip
                                  label="Override"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{
                                    ml: 1,
                                    fontSize: "0.6rem",
                                    height: "16px",
                                  }}
                                />
                              )}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1 }}
                          >
                            Requester: {req.requesterName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{
                              display: "block",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            Click for details and actions
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
                {requests.length === 0 && !error && !loading && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      No maintenance requests found.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        )}

        {/* HOD Management Tab */}
        {activeTab === 1 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <SupervisorAccountIcon /> Maintenance Head of Department
                Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openHodCreateDialog}
                sx={{
                  bgcolor: "success.main",
                  "&:hover": { bgcolor: "success.dark" },
                }}
              >
                Create New HOD
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Manage maintenance HODs who supervise specific categories like
                Electrical, Plumbing, HVAC, Carpentry, etc. These are separate
                from academic departments.
              </Typography>
            </Alert>

            {/* HOD List */}
            <Card>
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Maintenance Category</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Team Size</TableCell>
                      <TableCell>Experience</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hods.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">
                            No HODs found. Create your first HOD using the
                            button above.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      hods.map((hod) => (
                        <TableRow key={hod.id}>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <SupervisorAccountIcon color="primary" />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {hod.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {hod.qualifications}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={hod.category}
                              color="primary"
                              size="small"
                              icon={<BusinessIcon />}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <EmailIcon fontSize="small" /> {hod.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <PhoneIcon fontSize="small" /> {hod.phone}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {hod.currentTeamSize || 0} / {hod.maxTeamSize}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {hod.experienceYears} years
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={hod.isActive ? "Active" : "Inactive"}
                              color={hod.isActive ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Delete HOD">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  setDeleteHodDialog({
                                    open: true,
                                    hod: hod,
                                    loading: false,
                                  })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Worker Management Tab */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Maintenance Worker Management
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Manage maintenance workers assigned to specific categories like
                Electrical, Plumbing, HVAC, etc. These are maintenance staff,
                not academic department personnel.
              </Typography>
            </Alert>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">All Workers</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        setWorkerCreateDialog({ open: true, loading: false })
                      }
                      startIcon={<AddIcon />}
                    >
                      Create Worker
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={fetchWorkers}
                      startIcon={<RefreshIcon />}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Maintenance Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : workers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No workers found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      workers.map((worker) => (
                        <TableRow key={worker._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {worker.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {worker.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {worker.phone || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={worker.category || "N/A"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={worker.status || "Active"}
                              size="small"
                              color={
                                worker.status === "Active"
                                  ? "success"
                                  : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => openDeleteWorkerDialog(worker)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* HOD Creation Dialog */}
        <Dialog
          open={hodCreateDialog.open}
          onClose={closeHodCreateDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Maintenance Head of Department</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                label="Full Name"
                value={hodFormData.name}
                onChange={(e) => handleHodFormChange("name", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={hodFormData.email}
                onChange={(e) => handleHodFormChange("email", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Phone"
                value={hodFormData.phone}
                onChange={(e) => handleHodFormChange("phone", e.target.value)}
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={hodFormData.category}
                  onChange={(e) =>
                    handleHodFormChange("category", e.target.value)
                  }
                  label="Category"
                >
                  <MenuItem value="electrical">Electrical</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="hvac">HVAC</MenuItem>
                  <MenuItem value="general">General Maintenance</MenuItem>
                  <MenuItem value="cleaning">Cleaning</MenuItem>
                  <MenuItem value="security">Security</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Years of Experience"
                type="number"
                value={hodFormData.experienceYears}
                onChange={(e) =>
                  handleHodFormChange("experienceYears", e.target.value)
                }
                fullWidth
                required
              />
              <TextField
                label="Qualifications"
                multiline
                rows={3}
                value={hodFormData.qualifications}
                onChange={(e) =>
                  handleHodFormChange("qualifications", e.target.value)
                }
                fullWidth
                placeholder="Professional qualifications, certifications, etc."
              />
              <TextField
                label="Maximum Team Size"
                type="number"
                value={hodFormData.maxTeamSize}
                onChange={(e) =>
                  handleHodFormChange("maxTeamSize", e.target.value)
                }
                fullWidth
                required
                inputProps={{ min: 1, max: 50 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeHodCreateDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateHod}
              disabled={
                hodCreateDialog.loading ||
                !hodFormData.name ||
                !hodFormData.email ||
                !hodFormData.category
              }
              startIcon={
                hodCreateDialog.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AddIcon />
                )
              }
            >
              {hodCreateDialog.loading ? "Creating..." : "Create HOD"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Worker Creation Dialog */}
        <Dialog
          open={workerCreateDialog.open}
          onClose={() => setWorkerCreateDialog({ open: false, loading: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Maintenance Worker</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                label="Full Name"
                value={workerFormData.name}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={workerFormData.email}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label="Phone"
                value={workerFormData.phone}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label="Experience Years"
                type="number"
                value={workerFormData.experienceYears}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    experienceYears: e.target.value,
                  }))
                }
                fullWidth
                inputProps={{ min: 0, max: 50 }}
              />
              <TextField
                label="Qualifications"
                multiline
                rows={3}
                value={workerFormData.qualifications}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    qualifications: e.target.value,
                  }))
                }
                fullWidth
                placeholder="Professional qualifications, certifications, etc."
              />
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={workerFormData.category}
                  onChange={(e) =>
                    setWorkerFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  label="Category"
                >
                  <MenuItem value="electrical">Electrical</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="hvac">HVAC</MenuItem>
                  <MenuItem value="general">General Maintenance</MenuItem>
                  <MenuItem value="cleaning">Cleaning</MenuItem>
                  <MenuItem value="security">Security</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Contact Preference</InputLabel>
                <Select
                  value={workerFormData.contactPreference}
                  onChange={(e) =>
                    setWorkerFormData((prev) => ({
                      ...prev,
                      contactPreference: e.target.value,
                    }))
                  }
                  label="Contact Preference"
                >
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Max Concurrent Tasks"
                type="number"
                value={workerFormData.maxConcurrentTasks}
                onChange={(e) =>
                  setWorkerFormData((prev) => ({
                    ...prev,
                    maxConcurrentTasks: e.target.value,
                  }))
                }
                fullWidth
                inputProps={{ min: 1, max: 20 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setWorkerCreateDialog({ open: false, loading: false })
              }
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateWorker}
              disabled={
                workerCreateDialog.loading ||
                !workerFormData.name ||
                !workerFormData.email ||
                !workerFormData.phone ||
                !workerFormData.category
              }
              startIcon={
                workerCreateDialog.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AddIcon />
                )
              }
            >
              {workerCreateDialog.loading ? "Creating..." : "Create Worker"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialog.open}
        onClose={closeFilterDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">{filterDialog.title}</Typography>
            <IconButton onClick={closeFilterDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {filterDialog.requests.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No requests found for this category.
            </Alert>
          ) : (
            <List>
              {filterDialog.requests.map((req) => (
                <ListItem key={req.id} divider>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {req.tokenNumber}
                        </Typography>
                        <Chip
                          size="small"
                          label={req.status}
                          color={
                            req.status === "resolved"
                              ? "success"
                              : req.status === "pending"
                              ? "warning"
                              : req.status === "in_progress"
                              ? "secondary"
                              : "default"
                          }
                        />
                        {req.autoAssigned && (
                          <Tooltip title="Auto-assigned by system">
                            <Chip
                              label="Auto"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: "0.6rem", height: "20px" }}
                            />
                          </Tooltip>
                        )}
                        {req.manuallyOverridden && (
                          <Tooltip title="Manually overridden by manager">
                            <Chip
                              label="Override"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: "0.6rem", height: "20px" }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Category:</strong> {req.category} |{" "}
                          <strong>Subcategory:</strong>{" "}
                          {req.subcategory?.replace(/_/g, " ") || "N/A"} |{" "}
                          <strong>Location:</strong> {req.location}
                        </Typography>
                        <Typography
                          variant="body2"
                          noWrap
                          title={req.description}
                        >
                          <strong>Description:</strong> {req.description}
                        </Typography>
                        {req.assignedWorkerName && (
                          <Typography variant="body2">
                            <strong>Assigned to:</strong>{" "}
                            {req.assignedWorkerName}
                            {req.assignedAt && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (assigned{" "}
                                {new Date(req.assignedAt).toLocaleString()})
                              </Typography>
                            )}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Requester: {req.requesterName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Created: {new Date(req.createdAt).toLocaleString()}
                          {req.updatedAt && req.updatedAt !== req.createdAt && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary.main"
                              sx={{ ml: 2, fontWeight: "bold" }}
                            >
                              | Updated:{" "}
                              {new Date(req.updatedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog
        open={requestDetailDialog.open}
        onClose={closeRequestDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              Request Details: {requestDetailDialog.request?.tokenNumber}
            </Typography>
            <IconButton onClick={closeRequestDetailDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {requestDetailDialog.request && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={requestDetailDialog.request.status}
                      color={
                        requestDetailDialog.request.status === "resolved"
                          ? "success"
                          : requestDetailDialog.request.status === "pending"
                          ? "warning"
                          : requestDetailDialog.request.status === "in_progress"
                          ? "secondary"
                          : "default"
                      }
                    />
                    {requestDetailDialog.request.autoAssigned && (
                      <Tooltip title="Auto-assigned by system">
                        <Chip
                          label="Auto-Assigned"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {requestDetailDialog.request.manuallyOverridden && (
                      <Tooltip title="Manually overridden by manager">
                        <Chip
                          label="Manual Override"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.category}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subcategory
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.subcategory?.replace(
                      /_/g,
                      " "
                    ) || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.priority || "Medium"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.location}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.description}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Requester
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.requesterName} (
                    {requestDetailDialog.request.requesterEmail})
                  </Typography>
                </Grid>

                {requestDetailDialog.request.assignedWorkerName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assigned Worker
                    </Typography>
                    <Typography variant="body1">
                      {requestDetailDialog.request.assignedWorkerName}
                      {requestDetailDialog.request.assignedWorkerEmail &&
                        ` (${requestDetailDialog.request.assignedWorkerEmail})`}
                    </Typography>
                  </Grid>
                )}

                {requestDetailDialog.request.contactPhone && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Phone
                    </Typography>
                    <Typography variant="body1">
                      {requestDetailDialog.request.contactPhone}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {requestDetailDialog.request.createdAt
                      ? new Date(
                          requestDetailDialog.request.createdAt
                        ).toLocaleString()
                      : "Unknown"}
                  </Typography>
                </Grid>

                {requestDetailDialog.request.assignedAt && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assigned At
                    </Typography>
                    <Typography variant="body1">
                      {new Date(
                        requestDetailDialog.request.assignedAt
                      ).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                }}
              >
                <Button variant="outlined" onClick={closeRequestDetailDialog}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    openManagementDialog(requestDetailDialog.request);
                  }}
                >
                  Manage Request
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Management Dialog */}
      <Dialog
        open={managementDialog.open}
        onClose={closeManagementDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              Manage Request: {managementDialog.request?.tokenNumber}
            </Typography>
            <IconButton onClick={closeManagementDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {managementDialog.request && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                {/* Request Overview */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Request Overview
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={managementDialog.request.status}
                      color={
                        managementDialog.request.status === "resolved"
                          ? "success"
                          : managementDialog.request.status === "pending"
                          ? "warning"
                          : managementDialog.request.status === "in_progress"
                          ? "secondary"
                          : "default"
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      Category: {managementDialog.request.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subcategory:{" "}
                      {managementDialog.request.subcategory?.replace(
                        /_/g,
                        " "
                      ) || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Priority: {managementDialog.request.priority || "Medium"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    <strong>Description:</strong>{" "}
                    {managementDialog.request.description}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Location:</strong>{" "}
                    {managementDialog.request.location}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Requester:</strong>{" "}
                    {managementDialog.request.requesterName} (
                    {managementDialog.request.requesterEmail})
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Management Controls */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Management Actions
                  </Typography>
                </Grid>

                {/* Worker Assignment */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assign Worker</InputLabel>
                    <Select
                      value={managementDialog.selectedWorker}
                      onChange={(e) =>
                        setManagementDialog((prev) => ({
                          ...prev,
                          selectedWorker: e.target.value,
                        }))
                      }
                      label="Assign Worker"
                      disabled={managementDialog.loading}
                    >
                      <MenuItem value="">
                        <em>No worker assigned</em>
                      </MenuItem>
                      {managementDialog.workers.map((worker) => (
                        <MenuItem key={worker.id} value={worker.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <PersonIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">
                                {worker.name} ({worker.email})
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Specializations:{" "}
                                {worker.specializations?.join(", ") || "None"}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Update */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Update Status</InputLabel>
                    <Select
                      value={managementDialog.newStatus}
                      onChange={(e) =>
                        setManagementDialog((prev) => ({
                          ...prev,
                          newStatus: e.target.value,
                        }))
                      }
                      label="Update Status"
                      disabled={managementDialog.loading}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="assigned">Assigned</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Management Notes"
                    value={managementDialog.notes}
                    onChange={(e) =>
                      setManagementDialog((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Add notes about this management action..."
                    disabled={managementDialog.loading}
                  />
                </Grid>

                {/* Current Assignment Info */}
                {managementDialog.request.assignedWorkerName && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Currently Assigned To:
                      </Typography>
                      <Typography variant="body1">
                        {managementDialog.request.assignedWorkerName}
                        {managementDialog.request.assignedWorkerEmail &&
                          ` (${managementDialog.request.assignedWorkerEmail})`}
                      </Typography>
                      {managementDialog.request.assignedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Assigned on:{" "}
                          {new Date(
                            managementDialog.request.assignedAt
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeManagementDialog}
            disabled={managementDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={managementDialog.loading}
            startIcon={
              managementDialog.loading ? (
                <CircularProgress size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {managementDialog.loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete HOD Confirmation Dialog */}
      <Dialog
        open={deleteHodDialog.open}
        onClose={() =>
          setDeleteHodDialog({ open: false, hod: null, loading: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ mr: 1, color: "error.main" }} />
            Confirm HOD Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteHodDialog.hod && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete the following Head of
                Department?
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  my: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {deleteHodDialog.hod.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Email: {deleteHodDialog.hod.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Maintenance Category: {deleteHodDialog.hod.category}
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> This action cannot be undone. The
                  HOD will be permanently removed and all their managed workers
                  and assignments will be affected.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteHodDialog({ open: false, hod: null, loading: false })
            }
            disabled={deleteHodDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteHod}
            variant="contained"
            color="error"
            disabled={deleteHodDialog.loading}
            startIcon={
              deleteHodDialog.loading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteHodDialog.loading ? "Deleting..." : "Delete HOD"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Worker Deletion Dialog */}
      <Dialog
        open={deleteWorkerDialog.open}
        onClose={() =>
          setDeleteWorkerDialog({ open: false, worker: null, loading: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Confirm Worker Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteWorkerDialog.worker && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this worker? This action cannot
                be undone.
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Worker Details:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {deleteWorkerDialog.worker.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email:</strong> {deleteWorkerDialog.worker.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Maintenance Category:</strong>{" "}
                  {deleteWorkerDialog.worker.category || "N/A"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() =>
              setDeleteWorkerDialog({
                open: false,
                worker: null,
                loading: false,
              })
            }
            disabled={deleteWorkerDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteWorker}
            variant="contained"
            color="error"
            disabled={deleteWorkerDialog.loading}
            startIcon={
              deleteWorkerDialog.loading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteWorkerDialog.loading ? "Deleting..." : "Delete Worker"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ManagerDashboard;
